import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { GHG_Assessment } from "../db_access/ghg_assessment";
import { LongTermTool } from "../db_access/long_term_tool";
import { NetZeroTool } from "../db_access/net_zero_tool";
import { ShortTermTool } from "../db_access/short_term_tool";
import { Structure } from "../db_access/structure";
import { Supply_chain } from "../db_access/supply_chain";
import { buildOrganizationReport } from "../reports/model/buildOrganizationReport";
import { HTML_REPORT_FEATURES } from "../reports/reportConfig";
import { ReportRuntimeUnavailableError, generateHtmlReport } from "../reports/generateHtmlReport";

const NET_ZERO_HORIZONS = ["short-term", "long-term"];

// Per entity type: display kind and the db_access calls that load report context.
const ENTITY_SOURCES = {
  [Structure.label]: {
    kind: "Organization",
    ghg: (id) => GHG_Assessment.get_by_structure_id(id),
    shortTerm: (id) => ShortTermTool.get_by_structure_id(id),
    longTerm: (id) => LongTermTool.get_by_structure_id(id),
    actions: (id, horizon) => NetZeroTool.get_by_structure_id_and_type(id, horizon),
  },
  [Supply_chain.label]: {
    kind: "Supply chain",
    ghg: (id) => GHG_Assessment.get_by_supply_chain_id(id),
    shortTerm: (id) => ShortTermTool.get_by_supply_chain_id(id),
    longTerm: (id) => LongTermTool.get_by_supply_chain_id(id),
    actions: (id, horizon) => NetZeroTool.get_by_supply_chain_id_and_type(id, horizon),
  },
};

// Optional context: a failed request leaves that section empty instead of failing the report.
const readJson = async (request, fallback) => {
  try {
    const response = await request;
    if (!response.ok) return fallback;
    return (await response.json()) ?? fallback;
  } catch {
    return fallback;
  }
};

/**
 * Generates the interactive HTML report for a Structure or Supply Chain.
 *
 * @param {string} entityType - Structure.label or Supply_chain.label
 * @param {object} entity - selected Structure / Supply Chain record ({ id, name, year })
 * @param {Array} emissions
 * @param {Array} emissionDetails
 */
const useOrganizationHtmlReport = ({ entityType, entity, emissions, emissionDetails }) => {
  const { t, i18n } = useTranslation();
  const { emissionDetailOptions } = useContextProvider();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const generate = useCallback(async () => {
    const source = ENTITY_SOURCES[entityType];
    if (!source || !entity?.id) {
      setError(t("Select an organization before generating the report."));
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const includeBrsr = HTML_REPORT_FEATURES.brsr;
      // Targets and reduction actions only appear in the BRSR content.
      const [ghgAssessment, shortTermTargets, longTermTargets, ...actionGroups] = await Promise.all([
        readJson(source.ghg(entity.id), null),
        includeBrsr ? readJson(source.shortTerm(entity.id), []) : [],
        includeBrsr ? readJson(source.longTerm(entity.id), []) : [],
        ...(includeBrsr ? NET_ZERO_HORIZONS.map((horizon) => readJson(source.actions(entity.id, horizon), [])) : []),
      ]);

      const model = buildOrganizationReport({
        entity,
        entityKind: source.kind,
        emissions,
        emissionDetails,
        emissionDetailOptions,
        ghgAssessment,
        shortTermTargets,
        longTermTargets,
        reductionActions: actionGroups.flat(),
        features: HTML_REPORT_FEATURES,
        translate: t,
        language: i18n.language,
      });
      await generateHtmlReport({ model, translate: t, fileLabel: includeBrsr ? "BRSR_Report" : "Emissions_Report" });
    } catch (err) {
      console.error("Error generating HTML report:", err);
      setError(
        err instanceof ReportRuntimeUnavailableError
          ? t("The report engine is not available on this server. Please contact support.")
          : t("The HTML report could not be generated. Please try again.")
      );
    } finally {
      setIsGenerating(false);
    }
  }, [entityType, entity, emissions, emissionDetails, emissionDetailOptions, t, i18n.language]);

  return { generate, isGenerating, error, clearError: () => setError(null) };
};

export default useOrganizationHtmlReport;
